import { TypedDocumentNode, gql } from '@apollo/client';
import { ComponentProps, ReactNode } from 'react';

import {
  So5LeaderboardSeasonality,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { TeamLineupLayout } from 'layouts/TeamLineupLayout';

import { ContenderPage_so5LeaderboardContender } from './__generated__/index.graphql';

type Props = {
  so5LeaderboardContender: ContenderPage_so5LeaderboardContender;
  children: ReactNode;
} & Pick<
  ComponentProps<typeof TeamLineupLayout>,
  'onClickGame' | 'onClickRanking' | 'onLoadPreviousLineup'
>;

export const ContenderPage = ({
  so5LeaderboardContender,
  children,
  ...teamLineupLayoutProps
}: Props) => {
  const { sport } = useSportContext();

  const { seasonality } = so5LeaderboardContender.so5Leaderboard;
  const seasonalityStadiumUrl =
    seasonality === So5LeaderboardSeasonality.IN_SEASON
      ? 'inSeasonStadiumUrl'
      : 'allSeasonsStadiumUrl';

  const bgUrl =
    sport === Sport.FOOTBALL
      ? so5LeaderboardContender.so5Leaderboard.stadiumUrl
      : so5LeaderboardContender.so5Leaderboard.so5LeaderboardGroup[
          seasonalityStadiumUrl
        ];

  return (
    <TeamLineupLayout
      so5LeaderboardContender={so5LeaderboardContender}
      backgroundUrl={bgUrl || undefined}
      {...teamLineupLayoutProps}
    >
      {children}
    </TeamLineupLayout>
  );
};

ContenderPage.fragments = {
  so5LeaderboardContender: gql`
    fragment ContenderPage_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        slug
        stadiumUrl
        seasonality
        so5LeaderboardGroup {
          slug
          inSeasonStadiumUrl: stadiumUrl(seasonality: IN_SEASON)
          allSeasonsStadiumUrl: stadiumUrl(seasonality: ALL_SEASONS)
        }
      }
      ...TeamLineupLayout_so5LeaderboardContender
    }
    ${TeamLineupLayout.fragments.so5LeaderboardContender}
  ` as TypedDocumentNode<ContenderPage_so5LeaderboardContender>,
};
